import { useMemo, useState } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useTranslation } from "@/translations";

import { TemplateFormValues, TemplateItem, TemplateManagementConfig } from "../TemplateManagement.types";
import { templateValidationSchema } from "../templateValidationSchema";

export function useTemplateManagement(config: TemplateManagementConfig) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const { showYesNoDialog, showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const { data: templates = [] } = useQuery({
    queryKey: [config.queryKey],
    queryFn: () => config.api.getAll(),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const selectedTemplate = useMemo(
    () => templates.find((tmpl) => tmpl.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  const initialValues = useMemo<TemplateFormValues>(() => {
    const source = isCreating ? null : selectedTemplate;
    return {
      title: source?.title ?? "",
      content: source?.content ?? "",
      subject: source?.subject ?? "",
    };
  }, [isCreating, selectedTemplate]);

  const createMutation = useMutation({
    mutationFn: (values: TemplateFormValues) => config.api.create(values),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      setSelectedTemplateId(created.id);
      setIsCreating(false);
      showSuccess(config.labels.createSuccess);
    },
    onError: () => {
      showError(config.labels.createError);
    },
  });

  const saveMutation = useMutation({
    mutationFn: (values: TemplateFormValues) => {
      if (selectedTemplateId === null) {
        throw new Error("No template selected for update");
      }
      return config.api.update(selectedTemplateId, values);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      showSuccess(config.labels.saveSuccess);
    },
    onError: () => {
      showError(config.labels.saveError);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (selectedTemplateId === null) {
        throw new Error("No template selected for delete");
      }
      return config.api.remove(selectedTemplateId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      setSelectedTemplateId(null);
      showSuccess(config.labels.deleteSuccess);
    },
    onError: () => {
      showError(config.labels.deleteError);
    },
  });

  const validationSchema = useMemo(
    () => templateValidationSchema(t, config.labels, config.titleMaxLength, config.subject),
    [t, config.labels, config.titleMaxLength, config.subject]
  );

  const formik = useFormikWithDirtyForm<TemplateFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => (isCreating ? createMutation.mutateAsync(values) : saveMutation.mutateAsync(values)),
  });

  const runAfterDiscardConfirmation = (action: () => void) => {
    if (!formik.dirty) {
      action();
      return;
    }

    showConfirmationDialog({
      dialogTitleText: t("common.formDialog.unsavedChangesTitle"),
      dialogText: t("common.formDialog.unsavedChangesText"),
      agreeText: t("common.formDialog.discardChanges"),
      discardText: t("common.cancel"),
      onClose: (confirmed) => {
        if (confirmed) {
          formik.resetForm();
          action();
        }
      },
    });
  };

  const handleSelectTemplate = (template: TemplateItem | null) => {
    runAfterDiscardConfirmation(() => {
      setIsCreating(false);
      setSelectedTemplateId(template?.id ?? null);
    });
  };

  const handleAddNew = () => {
    runAfterDiscardConfirmation(() => {
      setSelectedTemplateId(null);
      setIsCreating(true);
    });
  };

  const handleCancelCreate = () => {
    runAfterDiscardConfirmation(() => setIsCreating(false));
  };

  const handleDelete = () => {
    showYesNoDialog(config.labels.deleteConfirmTitle, config.labels.deleteConfirmText, (confirmed) => {
      if (confirmed) {
        deleteMutation.mutate();
      }
    });
  };

  return {
    isCreating,
    templates,
    selectedTemplate,
    formik,
    isSaving: saveMutation.isPending || createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    handleSelectTemplate,
    handleAddNew,
    handleCancelCreate,
    handleDelete,
  };
}
